// Slide-over account panel: home → menu, orders, subscription, nutrition,
// address, family, profile. Data comes from /orders/:token/account-overview,
// /payment-history/:token and /orders/:token/subscriptions, all behind the
// ownership gate.

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';
import { COLORS, FONTS } from 'styles/theme';
import { useFlow } from 'context/FlowContext';
import { useAuthActions, SwitchProfileButton } from 'components/auth/AuthActions';
import { chooseProfile } from 'utils/activeProfile';
import { fetchMenu, sendQuizInvite } from 'services/publicFlowService';
import { isValidPhone } from 'utils/phoneFormat';
import DeliveryAddressFields from 'components/shared/DeliveryAddressFields';
import SubscriptionScreen from 'components/auth/SubscriptionScreen';
import SkipDeliveriesModal from 'components/mealplan/SkipDeliveriesModal';
import { CalendarIcon } from 'components/mealplan/DeliveryDateTabs';
import { MacroTargetCard, MacroTargetEditor, MealRestrictionEditor, cardLinkStyle } from 'components/shared/MacroTargets';
import 'styles/storefront-tokens.css';
import 'components/storefront/flow.css';

const { dark } = COLORS;
const MUTED = '#8a8d91';
const HAIR = '1px solid #ececec';

const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '15px',
  border: '1px solid #d8d0bd', borderRadius: '8px', background: '#fff',
  color: dark, fontFamily: FONTS.sans,
};

const SAVE_BTN = (busy) => ({
  padding: '12px 20px', border: 'none', borderRadius: '8px', background: '#f9a826',
  color: '#fff', fontWeight: 700, fontSize: '15px', fontFamily: FONTS.sans,
  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
});

const LINK_BTN = (color) => ({
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', color,
  fontSize: '10px', fontWeight: 700, textDecoration: 'underline',
  fontFamily: FONTS.sans, textAlign: 'left',
});

// Same weight as the Nutrition screen's "ALLERGIES & RESTRICTIONS" title.
const sectionHeading = (text) => (
  <div style={{ fontSize: '11px', letterSpacing: '1px', fontWeight: 700, color: dark, margin: '14px 0 0' }}>{text}</div>
);

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const MEAL_ORDER = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Snacks'];

// Collapses duplicate Client rows to one entry per meal, in eating order.
// Main meals with no row show at 0 so they can be switched on; saving creates the row.
const MAIN_MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

// The Subscription screen is unfinished: its entry row shows only in builds with REACT_APP_SHOW_SUBSCRIPTION=true.
const SHOW_SUBSCRIPTION = process.env.REACT_APP_SHOW_SUBSCRIPTION === 'true';
const EMPTY_TARGETS = { calories: 0, proteinG: 0, carbsG: 0, fatG: 0, fiberG: 0 };

function orderedMealTargets(list = []) {
  const byMeal = new Map();
  (list || []).forEach((m) => {
    if (m?.meal && !byMeal.has(m.meal)) byMeal.set(m.meal, m);
  });
  if (byMeal.size) {
    MAIN_MEALS.forEach((meal) => {
      const has = byMeal.has(meal) || (meal === 'Snack' && byMeal.has('Snacks'));
      if (!has) byMeal.set(meal, { meal, ...EMPTY_TARGETS });
    });
  }
  return [...byMeal.values()].sort((a, b) => {
    const ai = MEAL_ORDER.indexOf(a.meal);
    const bi = MEAL_ORDER.indexOf(b.meal);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

// One allergy group per meal row; rows with no Meal fall back to a single
// all-meals group, which saves without a meal.
function orderedRestrictionGroups(overview) {
  const byMeal = overview.restrictionsByMeal || [];
  if (!byMeal.length) return [{ meal: null, restrictions: overview.restrictions || [] }];
  return [...byMeal].sort((a, b) => {
    const ai = MEAL_ORDER.indexOf(a.meal);
    const bi = MEAL_ORDER.indexOf(b.meal);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

// True when a rethrown allSettled reason carried a 401.
function ov401(err) { return err?.status === 401 || /session has expired/i.test(err?.message || ''); }

// Best human text out of an axios error: `message` over the `error` code, plus
// `detail` (the Airtable message) where the backend sends it.
function serverMessage(err) {
  if (err?.code === 'ECONNABORTED') {
    return 'The server took too long to respond. It may be starting up or having trouble reaching Airtable.';
  }
  if (err?.request && !err?.response) {
    return "Couldn't reach the server. Is the backend running on port 3001?";
  }
  // A JWT rejection carries no JSON body, so it needs its own message.
  if (err?.response?.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  const data = err?.response?.data;
  if (!data) return '';
  const base = data.message || data.error || '';
  return data.detail ? `${base} (${data.detail})` : base;
}
// withYear for Squarespace-era orders: those run back years, where "Sat, Mar 12"
// alone is ambiguous.
const fmtDate = (iso, withYear) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
    });
  } catch { return iso; }
};

// Airtable's UTC `Created` stamp as "01/09/2026 13:29 EST" (dd/mm/yyyy, always Eastern).
const fmtCreated = (iso) => {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const stamp = at.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return `${stamp.replace(',', '')} EST`;
};

// Loading placeholder shaped like the account home. The keyframes are injected
// here because the panel has no stylesheet and inline styles can't express them.
function AccountSkeleton() {
  const bar = (w, h = 14) => ({
    width: w, height: h, borderRadius: '6px', background: COLORS.creamAlt,
    animation: 'sfSkeleton 1.4s ease-in-out infinite',
  });
  return (
    <div aria-busy="true" aria-live="polite">
      <style>{`
        @keyframes sfSkeleton { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }
        @media (prefers-reduced-motion: reduce) {
          [aria-busy="true"] * { animation: none !important }
        }
      `}</style>
      <span className="sf-sr-only" style={{
        position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)',
      }}>
        Loading your account
      </span>

      <div style={{
        padding: '32px 16px 28px', background: COLORS.creamAlt,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
      }}>
        <div style={{ ...bar('160px', 26), background: '#e2d8cd' }} />
        <div style={{ ...bar('60px', 11), background: '#e2d8cd' }} />
      </div>

      <div style={{ padding: '16px', display: 'grid', gap: '10px' }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: COLORS.cream, border: `1px solid ${COLORS.creamAlt}`,
              borderRadius: '12px', padding: '18px', display: 'grid', gap: '8px',
              animationDelay: `${i * 120}ms`,   // staggered, so it reads as a list
            }}
          >
            <div style={{ ...bar('55%', 17), animationDelay: `${i * 120}ms` }} />
            <div style={{ ...bar('78%', 11), animationDelay: `${i * 120}ms` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// One navigation card on the account home; `accent` marks the primary action.
function Row({ title, sub, onClick, accent = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left',
        background: COLORS.cream, border: `1px solid ${COLORS.creamAlt}`,
        borderLeft: accent ? `4px solid ${COLORS.orange}` : `1px solid ${COLORS.creamAlt}`,
        borderRadius: '12px', padding: '16px 18px', cursor: onClick ? 'pointer' : 'default',
        fontFamily: FONTS.sans,
      }}
    >
      <span style={{ flex: 1 }}>
        <span style={{
          display: 'block', fontFamily: FONTS.serif, fontSize: '19px',
          fontWeight: 700, color: dark,
        }}>{title}</span>
        {sub && (
          <span style={{ display: 'block', fontSize: '13px', color: MUTED, marginTop: '3px' }}>
            {sub}
          </span>
        )}
      </span>
      <span aria-hidden="true" style={{ color: COLORS.orange, fontSize: '20px', lineHeight: 1 }}>›</span>
    </button>
  );
}

// PATCH /orders/:token/profile body key -> its field in overview.address.
const ADDRESS_PATCH_FIELDS = {
  address: 'line1',
  address2: 'line2',
  city: 'city',
  state: 'state',
  zip: 'zip',
  phone: 'phone',
  deliveryNotes: 'deliveryNotes',
};

export default function AccountPanel({ open, onClose, onSignOut }) {
  // screen: {name:'home'} | {name:'orders'} | {name:'order', date} | {name:'subscription'} | {name:'nutrition'} | {name:'profile'}
  const [screen, setScreen] = useState({ name: 'home' });
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [overview, setOverview] = useState(null);
  // Drives the per-delivery totals and receipts on the Orders screens.
  const [payments, setPayments] = useState(null);   // { paymentsByDate, paymentRecords }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [reordered, setReordered] = useState(null);
  const [form, setForm] = useState(null);       // draft for profile/address edits
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [family, setFamily] = useState(null);
  const [familyForm, setFamilyForm] = useState({ firstName: '', lastName: '', email: '' });
  const [familyBusy, setFamilyBusy] = useState(false);
  const [familyError, setFamilyError] = useState('');
  const [familyNotice, setFamilyNotice] = useState(''); // result of the last add — linked vs invited
  const [invites, setInvites] = useState({});             // email -> 'sending' | 'sent' | 'error'
  const [confirmRemove, setConfirmRemove] = useState(null); // member key awaiting confirmation
  const [via, setVia] = useState('own');                  // 'granted' when managing someone else's plan
  const [access, setAccess] = useState(null);             // { requests, granted } for an own profile
  const [accessBusy, setAccessBusy] = useState(null);     // key of the request/grant being changed
  const [accessError, setAccessError] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [editingMeal, setEditingMeal] = useState(null);  // meal name being edited
  const [mealDraft, setMealDraft] = useState(null);      // its in-progress targets
  const [restrictionDraft, setRestrictionDraft] = useState(null); // { meal, ids, labels, allMeals } while editing
  const [restrictionBusy, setRestrictionBusy] = useState(false);
  const [restrictionError, setRestrictionError] = useState('');
  const [needsLogin, setNeedsLogin] = useState(false);   // 401 -> offer sign-in, not retry
  const [subscriptions, setSubscriptions] = useState(null); // null = not loaded yet, [] = none active
  const [defaultMealTargets, setDefaultMealTargets] = useState({});
  const [subscriptionsError, setSubscriptionsError] = useState('');
  const { addToCart, householdMembers, updateHouseholdMember } = useFlow();

  // Skipped meals show these once added; same per-meal defaults /api/menu gives the builder.
  useEffect(() => {
    if (screen.name !== 'subscription' || Object.keys(defaultMealTargets).length) return;
    fetchMenu({}).then(({ defaultMealTargets: d }) => setDefaultMealTargets(d || {}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.name]);

  const { handleLogin, dismissOnboarding, openProfilePicker, profiles, refreshProfiles } = useAuthActions();
  const { user } = useAuth0();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNeedsLogin(false);
    try {
      // Explicit timeouts: a hanging backend otherwise leaves the skeleton up forever.
      const { data } = await api.get('/auth/token', { timeout: 15000 });
      const auth = chooseProfile(data);
      // Several profiles and none picked yet: the picker reopens this panel once one is.
      if (!auth && data?.profiles?.length) {
        openProfilePicker();
        onClose();
        return;
      }
      if (!auth?.token) throw new Error('No meal plan is linked to this account yet.');
      setToken(auth.token);
      const ownProfile = auth.via !== 'granted';
      setVia(ownProfile ? 'own' : 'granted');
      const [ov, ph, fm, sub, acc] = await Promise.allSettled([
        api.get(`/orders/${auth.token}/account-overview`, { timeout: 25000 }),
        api.get(`/payment-history/${auth.token}`, { timeout: 25000 }),
        api.get(`/orders/${auth.token}/family-members`, { timeout: 25000 }),
        api.get(`/orders/${auth.token}/subscriptions`, { timeout: 25000 }),
        ownProfile ? api.get('/account/access', { timeout: 25000 }) : Promise.resolve(null),
      ]);
      if (ov.status === 'fulfilled' && ov.value.data.success) {
        setOverview(ov.value.data);
      } else {
        // allSettled swallows the rejection, so the cause has to come from ov.reason.
        const err = new Error(serverMessage(ov.reason) || ov.value?.data?.error || 'Could not load your account.');
        // Carried through so the caller can spot a 401 without matching on text.
        err.status = ov.reason?.response?.status;
        throw err;
      }
      if (ph.status === 'fulfilled') setPayments(ph.value.data);
      if (fm.status === 'fulfilled' && fm.value.data.success) setFamily(fm.value.data.members || []);
      setAccess(acc.status === 'fulfilled' && acc.value?.data?.success
        ? { requests: acc.value.data.requests || [], granted: acc.value.data.granted || [] }
        : null);
      if (sub.status === 'fulfilled' && sub.value.data.success) {
        setSubscriptions(sub.value.data.subscriptions || []);
        setSubscriptionsError('');
      } else {
        setSubscriptionsError(serverMessage(sub.reason) || sub.value?.data?.error || 'Could not load your subscription.');
      }
    } catch (e) {
      setNeedsLogin(e?.response?.status === 401 || ov401(e));
      setError(serverMessage(e) || e.message || 'Could not load your account.');
    } finally {
      setLoading(false);
    }
  }, [openProfilePicker, onClose]);

  useEffect(() => {
    if (open && !overview && !loading) load();
    if (open) {
      setScreen({ name: 'home' }); setForm(null); setSaveError('');
      setFamilyError(''); setFamilyNotice(''); setConfirmRemove(null);
      setAccessError(''); setConfirmRevoke(null);
      setEditingMeal(null); setMealDraft(null);
      setRestrictionDraft(null); setRestrictionError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const saveProfile = async (patch, onDone) => {
    setSaving(true);
    setSaveError('');
    try {
      const { data } = await api.patch(`/orders/${token}/profile`, patch);
      if (!data?.success) throw new Error(data?.error || 'Could not save.');
      // Retires the "we guessed your name from your email" banner (AuthActions).
      if (String(patch.firstName || '').trim()) dismissOnboarding();
      if (['firstName', 'lastName', 'email'].some((k) => patch[k] !== undefined)) refreshProfiles();
      // Merged locally rather than refetched — see saveMealTargets. The Address
      // screen re-derives its draft from overview.address, so this has to land.
      setOverview((prev) => {
        if (!prev) return prev;
        // The endpoint's normalisation, mirrored, so this shows what was stored.
        const str = (v) => String(v ?? '').trim();
        const next = { ...prev };

        if (['firstName', 'lastName', 'email'].some((k) => patch[k] !== undefined)) {
          next.profile = {
            ...prev.profile,
            ...(patch.firstName !== undefined ? { firstName: str(patch.firstName) } : {}),
            ...(patch.lastName !== undefined ? { lastName: str(patch.lastName) } : {}),
            ...(patch.email !== undefined ? { email: str(patch.email).toLowerCase() } : {}),
          };
        }

        const addressPatch = {};
        for (const [key, field] of Object.entries(ADDRESS_PATCH_FIELDS)) {
          if (patch[key] === undefined) continue;
          addressPatch[field] = key === 'state' ? str(patch[key]).toUpperCase() : str(patch[key]);
        }
        if (Object.keys(addressPatch).length) {
          next.address = { ...prev.address, ...addressPatch };
          // Retires the "address from your last delivery" hint.
          if (next.address.line1) next.addressSource = 'client';
        }
        return next;
      });
      onDone?.();
    } catch (e) {
      setSaveError(serverMessage(e) || e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  // Writes one meal's macro targets. The backend re-checks the caps and the
  // 4/4/9 identity, so its rejection is worth showing verbatim.
  const saveMealTargets = async (meal, draft) => {
    setSaving(true);
    setSaveError('');
    try {
      const { data } = await api.patch(`/orders/${token}/nutrition`, {
        meal,
        calories: Number(draft.calories) || 0,
        proteinG: Number(draft.proteinG) || 0,
        carbsG: Number(draft.carbsG) || 0,
        fatG: Number(draft.fatG) || 0,
        fiberG: Number(draft.fiberG) || 0,
        ...(draft.snacksPerDay != null ? { snacksPerDay: draft.snacksPerDay } : {}),
      });
      if (!data?.success) throw new Error(data?.error || 'Could not save.');
      // Patched in locally: a full load() right after the write trips Airtable's
      // 5 req/s cap and times out, and the server returns what it stored anyway.
      const saved = data.targets || draft;
      setOverview((prev) => {
        if (!prev) return prev;
        const list = prev.nutritionByMeal || [];
        return {
          ...prev,
          nutritionByMeal: list.some((m) => m.meal === meal)
            ? list.map((m) => (m.meal === meal ? { ...m, ...saved } : m))
            : [...list, { meal, ...saved }],
        };
      });
      // The menu's meal cards read the member held in the flow context, not this panel's
      // overview, so the edit is carried there too or the cards keep the old numbers.
      const primary = householdMembers?.[0];
      if (primary) {
        updateHouseholdMember(primary.id, {
          editedTargets: {
            ...(primary.editedTargets || {}),
            [meal]: {
              calories: Number(saved.calories) || 0,
              proteinG: Number(saved.proteinG) || 0,
              carbsG: Number(saved.carbsG) || 0,
              fatG: Number(saved.fatG) || 0,
              fiberG: Number(saved.fiberG) || 0,
            },
          },
        });
      }
      setEditingMeal(null);
      setMealDraft(null);
    } catch (e) {
      setSaveError(serverMessage(e) || e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  // Replaces the whole set for one meal, or for every Client row when no meal is sent.
  const saveRestrictions = async () => {
    setRestrictionBusy(true);
    setRestrictionError('');
    try {
      const { meal, ids, allMeals } = restrictionDraft;
      const { data } = await api.patch(`/orders/${token}/restrictions`, {
        restrictionIds: ids,
        ...(meal && !allMeals ? { meal } : {}),
      });
      if (!data?.success) throw new Error(data?.error || 'Could not save.');
      await load();
      setRestrictionDraft(null);
    } catch (e) {
      setRestrictionError(serverMessage(e) || e.message || 'Could not save your allergies.');
    } finally {
      setRestrictionBusy(false);
    }
  };

  const addFamilyMember = async () => {
    setFamilyBusy(true);
    setFamilyError('');
    setFamilyNotice('');
    try {
      const { data } = await api.post(`/orders/${token}/family-members`, familyForm);
      if (!data?.success) throw new Error(data?.error || 'Could not add that family member.');
      const { firstName, lastName, email } = familyForm;
      const name = [firstName, lastName].filter(Boolean).join(' ');
      setFamilyForm({ firstName: '', lastName: '', email: '' });

      // The backend already tells apart "this email belongs to someone who's
      // ordered before" (found by firstName+email, linked as-is, no invite —
      // they already have a nutrition profile) from "brand new person" (a
      // placeholder row was created and they need the quiz to build one).
      // alreadyLinked catches re-submitting someone already in the household.
      if (data.alreadyLinked) {
        setFamilyNotice(`${name} is already in your family members.`);
      } else if (data.inviteNeeded && email) {
        setFamilyNotice(`${name} is new to Square Fare — sending them a quiz invite so we can tailor their meals.`);
        await inviteToQuiz({ name, email, hasProfile: false });
      } else {
        setFamilyNotice(`Found ${name}'s existing Square Fare profile and added them to your household.`);
      }

      const { data: list } = await api.get(`/orders/${token}/family-members`);
      if (list?.success) setFamily(list.members || []);
    } catch (e) {
      setFamilyError(serverMessage(e) || e.message || 'Could not add that family member.');
    } finally {
      setFamilyBusy(false);
    }
  };

  const removeFamilyMember = async (m) => {
    setFamilyBusy(true);
    setFamilyError('');
    try {
      const { data } = await api.delete(`/orders/${token}/family-members`, {
        params: { firstName: m.firstName, email: m.email },
      });
      if (!data?.success) throw new Error(data?.error || 'Could not remove that family member.');
      setFamily((prev) => (prev || []).filter((x) => !(x.firstName === m.firstName && x.email === m.email)));
      setConfirmRemove(null);
    } catch (e) {
      setFamilyError(serverMessage(e) || e.message || 'Could not remove that family member.');
    } finally {
      setFamilyBusy(false);
    }
  };

  const setMemberAccess = (m, value) => setFamily((prev) => (prev || []).map((x) => (
    x.firstName === m.firstName && x.email === m.email ? { ...x, access: value } : x
  )));

  // Master side: ask to manage a household member's plan, or withdraw the ask / the access.
  const requestAccess = async (m) => {
    setAccessBusy(`${m.firstName}-${m.email}`);
    setFamilyError('');
    try {
      const { data } = await api.post(`/orders/${token}/family-members/access`, { firstName: m.firstName, email: m.email });
      if (!data?.success) throw new Error(data?.error || 'Could not send that request.');
      setMemberAccess(m, data.access);
    } catch (e) {
      setFamilyError(serverMessage(e) || e.message || 'Could not send that request.');
    } finally {
      setAccessBusy(null);
    }
  };

  const withdrawAccess = async (m) => {
    setAccessBusy(`${m.firstName}-${m.email}`);
    setFamilyError('');
    try {
      const { data } = await api.delete(`/orders/${token}/family-members/access`, {
        params: { firstName: m.firstName, email: m.email },
      });
      if (!data?.success) throw new Error(data?.error || 'Could not update access.');
      setMemberAccess(m, 'none');
    } catch (e) {
      setFamilyError(serverMessage(e) || e.message || 'Could not update access.');
    } finally {
      setAccessBusy(null);
    }
  };

  // Dependent side: approve, decline or revoke someone's access to this plan.
  const changeAccess = async (action, masterToken) => {
    setAccessBusy(`${action}-${masterToken}`);
    setAccessError('');
    try {
      const { data } = await api.post(`/account/access/${action}`, { masterToken });
      if (!data?.success) throw new Error(data?.error || 'Could not update access.');
      setAccess({ requests: data.requests || [], granted: data.granted || [] });
      setConfirmRevoke(null);
    } catch (e) {
      setAccessError(serverMessage(e) || e.message || 'Could not update access.');
    } finally {
      setAccessBusy(null);
    }
  };

  // Throws on failure so SubscriptionScreen can show the message; local state is
  // merged rather than refetched, like the other panel saves.
  const patchSubscription = async (recordId, d) => {
    try {
      const { data } = await api.patch(`/orders/${token}/subscriptions/${recordId}`, d);
      if (!data?.success) throw new Error(data?.error || 'Could not save.');
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not save.');
    }
    // Premium lives on the shared Client rows, so other deliveries may have changed too.
    if ('premium' in d) { await reloadSubscriptions(); return; }
    setSubscriptions((prev) => (prev || []).map((s) => {
      if (s.recordId !== recordId) return s;
      const next = { ...s };
      if ('deliveryDay' in d) next.deliveryDay = d.deliveryDay;
      if ('deliveryTime' in d) next.deliveryTime = d.deliveryTime;
      if ('mealsIncluded' in d) next.mealsIncluded = Number(d.mealsIncluded) || s.mealsIncluded;
      if ('address' in d) {
        next.address = {
          ...s.address,
          line1: d.address, line2: d.address2, city: d.city,
          state: String(d.state || '').toUpperCase(), zip: d.zip, phone: d.phone,
        };
      }
      return next;
    }));
  };

  const addDelivery = async (delivery) => {
    try {
      const { data } = await api.post(`/orders/${token}/subscriptions`, delivery);
      if (!data?.success) throw new Error(data?.error || 'Could not add that delivery day.');
      await reloadSubscriptions();
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not add that delivery day.');
    }
  };

  const reloadSubscriptions = async () => {
    const fresh = await api.get(`/orders/${token}/subscriptions`, { timeout: 25000 });
    if (fresh.data?.success) setSubscriptions(fresh.data.subscriptions || []);
  };

  // Skipping moves Next Delivery Date and can clear a week's meals, so both lists are refetched.
  const handleSkipsSaved = async () => {
    setShowSkipModal(false);
    try {
      const [, ov] = await Promise.all([
        reloadSubscriptions(),
        api.get(`/orders/${token}/account-overview`, { timeout: 25000 }),
      ]);
      if (ov.data?.success) setOverview(ov.data);
    } catch { /* the saved skips stand; the lists refresh on the next open */ }
  };

  const loadDeliveryOptions = async (zip) => {
    try {
      const { data } = await api.get(`/orders/${token}/delivery-options`, { params: zip ? { zip } : undefined, timeout: 25000 });
      if (!data?.success) throw new Error(data?.error || 'Could not load delivery days.');
      return data.days || [];
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not load delivery days.');
    }
  };

  const loadAddOnCatalog = async () => {
    try {
      const { data } = await api.get(`/orders/${token}/addon-catalog`, { timeout: 25000 });
      if (!data?.success) throw new Error(data?.error || 'Could not load add-ons.');
      return data.addOns || [];
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not load add-ons.');
    }
  };

  const addSubscriptionAddOn = async (templateRecordId, dishRecordId, quantity = 1) => {
    try {
      const { data } = await api.post(`/orders/${token}/subscriptions/add-on`, { templateRecordId, dishRecordId, quantity });
      if (!data?.success) throw new Error(data?.error || 'Could not add that add-on.');
      await reloadSubscriptions();
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not add that add-on.');
    }
  };

  const addSubscriptionMeal = async (templateRecordId, mealType, quantity, premium) => {
    try {
      const body = { templateRecordId, mealType, quantity, ...(typeof premium === 'boolean' ? { premium } : {}) };
      const { data } = await api.post(`/orders/${token}/subscriptions/meal`, body);
      if (!data?.success) throw new Error(data?.error || 'Could not add that meal.');
      await reloadSubscriptions();
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not add that meal.');
    }
  };

  const cancelSubscription = async (recordId) => {
    try {
      const { data } = await api.delete(`/orders/${token}/subscriptions/${recordId}`);
      if (!data?.success) throw new Error(data?.error || 'Could not cancel.');
    } catch (e) {
      throw new Error(serverMessage(e) || e.message || 'Could not cancel your subscription.');
    }
    setSubscriptions((prev) => (prev || []).filter((s) => s.recordId !== recordId));
  };

  const inviteToQuiz = async ({ name, email, hasProfile }) => {
    setInvites((prev) => ({ ...prev, [email]: 'sending' }));
    const res = await sendQuizInvite({
        email,
        name,
        invitedBy: overview?.profile?.firstName || '',
        hasProfile,
    });
    setInvites((prev) => ({ ...prev, [email]: res.success ? 'sent' : 'error' }));
  };

  if (!open) return null;

  const first = overview?.profile?.firstName || '';
  const orders = overview?.orders || [];
  const totalFor = (date) => payments?.paymentsByDate?.[date] ?? null;
  const receiptsFor = (date) => payments?.paymentRecords?.[date] || [];
  // Cards are per order, but payment history is keyed by delivery date. Use the
  // archived per-order total when there is one, and fall back to the date total
  // only when that date holds a single order — otherwise the split is unknown.
  const ordersOnDate = orders.reduce((m, o) => ({ ...m, [o.deliveryDate]: (m[o.deliveryDate] || 0) + 1 }), {});
  // An order can feed several people. One unnamed eater, or one who is the
  // account holder, needs no header — that is the ordinary single-person order.
  const eaterSections = (order) => {
    const items = order?.items || [];
    const eaters = order?.eaters || [];
    if (eaters.length < 2) return [{ key: 'all', showHeader: false, items }];
    const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return eaters.map((e) => ({
      key: norm(e.name) || '-',
      showHeader: true,
      name: e.name,
      email: e.email,
      itemCount: e.itemCount,
      isAccountHolder: e.isAccountHolder,
      items: items.filter((it) => norm(it.deliverTo) === norm(e.name)),
    }));
  };
  const cardTotal = (o) => (o.orderTotal != null
    ? o.orderTotal
    : (ordersOnDate[o.deliveryDate] === 1 ? totalFor(o.deliveryDate) : null));

  const reorder = async (item) => {
    const { dishes } = await fetchMenu({});
    const dish = (dishes || []).find((d) => String(d.dishId) === String(item.dishId) && d.mealType === item.mealType)
      || (dishes || []).find((d) => String(d.dishId) === String(item.dishId));
    if (!dish) { setReordered({ name: item.name, ok: false }); return; }
    addToCart(dish);
    setReordered({ name: item.name, ok: true });
    setTimeout(() => setReordered(null), 2000);
  };

  const header = (title, backTo) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', borderBottom: HAIR, fontFamily: FONTS.sans,
    }}>
      {backTo ? (
        <button type="button" onClick={() => setScreen(backTo)} style={{
          background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
          letterSpacing: '1px', color: dark, fontWeight: 700,
        }}>‹ BACK</button>
      ) : <span style={{ width: '52px' }} />}
      <span style={{ fontSize: '17px', color: dark }}>{title}</span>
      <button type="button" onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
        letterSpacing: '1px', color: dark, fontWeight: 700,
      }}>CLOSE</button>
    </div>
  );

  let body = null;

  if (loading || (!overview && !error)) {
    body = <>{header('Account')}<AccountSkeleton /></>;
  } else if (error) {
    // A 401 gets sign-in rather than Retry, which would only 401 again. Sign out
    // sits below either one — the greeting's link doesn't render on this screen.
    // The header's CLOSE is the only way out of this screen.
    body = (
      <>
      {header('Account')}
      <div style={{ padding: '40px 16px', textAlign: 'center', fontFamily: FONTS.sans }}>
        <p style={{ color: dark, fontSize: '15px' }}>{error}</p>
        {needsLogin ? (
          <button type="button" onClick={() => { onClose(); handleLogin(); }} style={{
            marginTop: '10px', background: COLORS.orange, border: 'none', borderRadius: '8px',
            padding: '11px 26px', cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: '15px',
          }}>Sign in</button>
        ) : (
          <button type="button" onClick={load} style={{
            marginTop: '10px', background: 'none', border: `1.5px solid ${dark}`, borderRadius: '8px',
            padding: '9px 22px', cursor: 'pointer', color: dark, fontWeight: 600,
          }}>Retry</button>
        )}
        <div>
          <button type="button" onClick={onSignOut} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: COLORS.text,
            fontSize: '13px', marginTop: '14px', textDecoration: 'underline',
            fontFamily: FONTS.sans,
          }}>Sign out</button>
          <div style={{ marginTop: '6px', fontFamily: FONTS.sans, color: COLORS.text }}>
            <SwitchProfileButton onClick={onClose} />
          </div>
        </div>
      </div>
      </>
    );
  } else if (screen.name === 'home') {
    const lastOrder = orders[0];
    // One Subscriptions row exists per meal type per delivery, so plans are counted by delivery slot.
    const deliverySlots = new Set((subscriptions || []).map((s) => `${s.deliveryDay}|${s.deliveryTime}`));
    const pendingRequests = via === 'own' ? access?.requests.length || 0 : 0;
    body = (
      <>
        {header('Account')}
        <div style={{ textAlign: 'center', padding: '32px 16px 28px', background: COLORS.creamAlt }}>
          <div style={{ fontFamily: FONTS.serif, fontSize: '30px', fontWeight: 700, color: dark }}>
            Hi, {first || 'there'}
          </div>
          {via === 'granted' && (
            <div style={{ fontSize: '13px', color: COLORS.text, marginTop: '6px', fontFamily: FONTS.sans }}>
             Granted Access
            </div>
          )}
          <button type="button" onClick={onSignOut} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: COLORS.text,
            fontSize: '13px', marginTop: '8px', textDecoration: 'underline',
            fontFamily: FONTS.sans,
          }}>Sign out</button>
          <div style={{ marginTop: '6px', fontFamily: FONTS.sans, color: COLORS.text }}>
            <SwitchProfileButton onClick={onClose} />
          </div>
        </div>
        <div style={{ padding: '16px', display: 'grid', gap: '10px' }}>
        <Row
          accent
          title="Your Menu This Week"
          sub="View and edit this week's meals"
          onClick={() => { onClose(); window.location.href = token ? `/meal-plan?customer=${token}` : '/meal-plan'; }}
        />
        <Row
          title="Orders"
          sub={lastOrder ? `Last delivery ${fmtDate(lastOrder.deliveryDate)} · ${lastOrder.itemCount} items` : 'No orders yet'}
          onClick={() => setScreen({ name: 'orders' })}
        />
        {SHOW_SUBSCRIPTION && (
          <Row
            title="Subscription"
            sub={subscriptions === null
              ? (subscriptionsError ? 'Could not load' : 'Loading…')
              : subscriptions.length === 0
                ? 'No active subscription'
                : deliverySlots.size === 1
                  ? `${subscriptions[0].deliveryDay} · ${subscriptions[0].deliveryTime}`
                  : `${deliverySlots.size} deliveries / week`}
            onClick={() => setScreen({ name: 'subscription' })}
          />
        )}
        {/* No entry point at all for clients tagged "No Nutrition Data in Sheet"
            — a greyed-out row would still advertise that the numbers exist. */}
        {!overview.hideNutrition && (
          <Row
            title="Nutrition Profile"
            sub={orderedMealTargets(overview.nutritionByMeal).length
              ? 'View and customize your nutrition targets and allergies'
              : 'Not set yet'}
            onClick={() => setScreen({ name: 'nutrition' })}
          />
        )}
        {/* Shown even when empty, or a tagged client has no way to add one. */}
        {overview.hideNutrition && (
          <Row
            title="Allergies & Restrictions"
            sub={overview.allergies?.length ? `${overview.allergies.length} recorded` : 'None recorded yet'}
            onClick={() => setScreen({ name: 'nutrition' })}
          />
        )}
        <Row
          title="Address"
          sub={overview.address?.line1
            ? [overview.address.line1, overview.address.city].filter(Boolean).join(', ')
            : 'No address saved'}
          onClick={() => setScreen({ name: 'address' })}
        />
        <Row
          title="Family Members"
          accent={pendingRequests > 0}
          sub={pendingRequests
            ? `${pendingRequests} ${pendingRequests === 1 ? 'person is' : 'people are'} asking to manage your plan`
            : family?.length
              ? `${family.length} ${family.length === 1 ? 'member' : 'members'}${family.some((m) => !m.hasProfile) ? ' · quiz pending' : ''}`
              : 'Add someone in your household'}
          onClick={() => setScreen({ name: 'family' })}
        />
        <Row
          title="Profile"
          sub={overview.profile.email}
          onClick={() => setScreen({ name: 'profile' })}
        />
        </div>
      </>
    );
  } else if (screen.name === 'orders') {
    body = (
      <>
        {header('Orders', { name: 'home' })}
        {orders.length === 0 && (
          <p style={{ padding: '30px 16px', textAlign: 'center', color: MUTED }}>No orders yet.</p>
        )}
        {orders.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setScreen({ name: 'order', id: o.id })}
            style={{
              display: 'block', width: '100%', textAlign: 'left', background: 'none',
              border: 'none', borderBottom: HAIR, padding: '16px', cursor: 'pointer', fontFamily: FONTS.sans,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{
                fontSize: '17px', fontWeight: 700, color: dark,
                fontVariantNumeric: 'tabular-nums', letterSpacing: '0.2px', overflowWrap: 'anywhere',
              }}>
                {o.orderId ? `Order ${o.orderId}` : fmtDate(o.deliveryDate, o.isHistorical)}
              </span>
              <span style={{ fontSize: '14px', color: MUTED, whiteSpace: 'nowrap', paddingLeft: '10px' }}>
                {cardTotal(o) != null ? money(cardTotal(o)) : ''}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: MUTED, marginTop: '2px' }}>
              {o.itemCount} {o.itemCount === 1 ? 'item' : 'items'}
              {o.isHistorical && ' · Squarespace order'}
            </div>
            {/* One order can feed several people; the count above covers all of them. */}
            {o.eaters?.length > 1 && (
              <div style={{ fontSize: '13px', color: MUTED, marginTop: '2px' }}>
                {o.eaters.map((e) => `${e.name || 'Unnamed'} ${e.itemCount}`).join(' · ')}
              </div>
            )}
            {o.createdAt && (
              <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
                Created at {fmtCreated(o.createdAt)}
              </div>
            )}
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
              {o.items.slice(0, 5).map((it, i) => (
                it.imageUrl
                  ? <span key={i} style={{
                      width: '44px', height: '44px', borderRadius: '6px', backgroundImage: `url(${it.imageUrl})`,
                      backgroundSize: 'cover', backgroundPosition: 'center', display: 'inline-block',
                    }} />
                  : <span key={i} style={{
                      width: '44px', height: '44px', borderRadius: '6px', background: '#f1ede4',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', color: MUTED,
                    }}>{(it.name || '?')[0]}</span>
              ))}
              {o.items.length > 5 && <span style={{ alignSelf: 'center', fontSize: '13px', color: MUTED }}>+{o.items.length - 5}</span>}
            </div>
          </button>
        ))}
      </>
    );
  } else if (screen.name === 'order') {
    const order = orders.find((o) => o.id === screen.id);
    const past = Boolean(order?.isHistorical);
    const receipts = receiptsFor(order?.deliveryDate);
    const ship = order?.shipTo;
    // The address this order was placed with, not the (editable) profile one.
    const shipLines = ship ? [
      order.deliverTo || ship.name,
      ship.line1,
      ship.line2,
      [ship.city, ship.state].filter(Boolean).join(', ') + (ship.zip ? ` ${ship.zip}` : ''),
      ship.phone,
    ].map((s) => String(s || '').trim()).filter(Boolean) : [];
    body = (
      <>
        {header(order?.orderId ? `Order ${order.orderId}` : fmtDate(order?.deliveryDate, past), { name: 'orders' })}
        <div style={{ padding: '14px 16px', borderBottom: HAIR, background: COLORS.cream, fontFamily: FONTS.sans }}>
          {(order?.orderIds?.length > 0 || order?.createdAt) && (
            <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: HAIR }}>
              <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED, marginBottom: '6px' }}>ORDER</div>
              {order.orderIds?.length > 0 && (
                <div style={{
                  fontSize: '13px', color: dark, fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.2px', overflowWrap: 'anywhere',
                }}>
                  {order.orderIds.length === 1 ? 'Order ' : 'Orders '}
                  {order.orderIds.join(', ')}
                </div>
              )}
              {order.createdAt && (
                <div style={{ fontSize: '13px', color: MUTED, marginTop: '3px' }}>
                  Created at {fmtCreated(order.createdAt)}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED, marginBottom: '6px' }}>
            {past ? 'ORDERED' : 'DELIVERY'}
          </div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: dark }}>
            {fmtDate(order?.deliveryDate, past)}
            {order?.deliveryTimes?.length ? ` · ${order.deliveryTimes.join(', ')}` : ''}
          </div>
          {shipLines.length > 0 && (
            <address style={{ fontStyle: 'normal', fontSize: '13px', color: MUTED, lineHeight: 1.6, marginTop: '8px' }}>
              {shipLines.map((l, i) => <div key={i}>{l}</div>)}
            </address>
          )}
          {ship?.deliveryNotes && (
            <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.6, marginTop: '8px' }}>
              <span style={{ fontWeight: 700 }}>Notes:</span> {ship.deliveryNotes}
            </div>
          )}
        </div>
        {eaterSections(order).map((section) => (
          <React.Fragment key={section.key}>
            {section.showHeader && (
              <div style={{
                padding: '12px 16px 6px', fontFamily: FONTS.sans, background: '#fbfaf7',
                borderBottom: HAIR,
              }}>
                <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED }}>
                  {section.isAccountHolder ? 'YOUR MEALS' : 'ALSO IN THIS ORDER'}
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: dark, marginTop: '3px' }}>
                  {section.name || 'Unnamed'}
                  <span style={{ fontSize: '13px', color: MUTED }}>
                    {` · ${section.itemCount} ${section.itemCount === 1 ? 'item' : 'items'}`}
                  </span>
                </div>
                {section.email && (
                  <div style={{ fontSize: '13px', color: MUTED, overflowWrap: 'anywhere' }}>{section.email}</div>
                )}
              </div>
            )}
            {section.items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', padding: '14px 16px', borderBottom: HAIR, fontFamily: FONTS.sans }}>
            {it.imageUrl && (
              <span style={{
                width: '52px', height: '52px', borderRadius: '6px', flexShrink: 0,
                backgroundImage: `url(${it.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center',
              }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', color: '#1a6ee0', fontWeight: 500 }}>{it.name}</div>
              <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
                Qty: {it.quantity} · {it.mealType}
              </div>
              {/* Plate order, protein first — so the meta line above doesn't repeat it. */}
              {it.ingredients?.length > 0 && (
                <div style={{ fontSize: '12px', color: dark, marginTop: '4px', lineHeight: 1.55 }}>
                  {it.ingredients.map((g) => g.name).join(' · ')}
                </div>
              )}
              {it.canReorder && (
                <button type="button" onClick={() => reorder(it)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: dark,
                  fontSize: '13px', fontWeight: 700, padding: '6px 0 0', textDecoration: 'underline',
                }}>Buy it again ›</button>
              )}
            </div>
          </div>
            ))}
          </React.Fragment>
        ))}
        <div style={{ padding: '16px', fontFamily: FONTS.sans }}>
          {/* The Stripe invoice, or the charge receipt when there's no invoice.
              Orders with neither fall back to the payment-history receipts. */}
          {order?.receiptUrl && (
            <a href={order.receiptUrl} target="_blank" rel="noopener noreferrer" style={{
              display: 'block', fontSize: '14px', color: '#1a6ee0', textDecoration: 'none',
            }}>View receipt →</a>
          )}
          {receipts.map((r, i) => r.receiptUrl && (
            <a key={i} href={r.receiptUrl} target="_blank" rel="noopener noreferrer" style={{
              display: 'block', fontSize: '14px', color: '#1a6ee0', marginTop: '6px', textDecoration: 'none',
            }}>View receipt →</a>
          ))}
          {!order?.receiptUrl && !receipts.some((r) => r.receiptUrl) && (
            <span style={{ fontSize: '13px', color: MUTED }}>
              No invoice on file for this delivery.
            </span>
          )}
        </div>
      </>
    );
  } else if (screen.name === 'nutrition') {
    const meals = orderedMealTargets(overview.nutritionByMeal);
    const restrictionGroups = orderedRestrictionGroups(overview);
    const groupFor = (meal) => restrictionGroups.find((g) => g.meal && g.meal === (meal === 'Snacks' ? 'Snack' : meal));
    // Allergies sit in the macro cards whenever there are cards to hold them.
    const restrictionsInCards = !overview.hideNutrition && meals.length > 0 && restrictionGroups.some((g) => g.meal);
    const canEditRestrictions = !overview.restrictionsUnavailable && !restrictionDraft;
    const startRestrictionEdit = (g) => {
      setRestrictionDraft({
        meal: g.meal,
        ids: g.restrictions.map((r) => r.id),
        labels: Object.fromEntries(g.restrictions.map((r) => [r.id, r.name])),
        allMeals: !g.meal,
      });
      setRestrictionError('');
    };
    const restrictionEditorFor = (g) => (restrictionDraft && restrictionDraft.meal === g.meal ? (
      <MealRestrictionEditor
        meal={g.meal || 'every'}
        draft={restrictionDraft}
        onChange={setRestrictionDraft}
        onSave={saveRestrictions}
        onCancel={() => { setRestrictionDraft(null); setRestrictionError(''); }}
        saving={restrictionBusy}
        error={restrictionError}
        showAllMeals={!!g.meal && restrictionGroups.length > 1}
      />
    ) : null);
    // No editor when the ids didn't load — saving would drop restrictions we only failed to read.
    const restrictionsUnavailableNote = overview.restrictionsUnavailable && (
      <p style={{ fontSize: '12px', color: MUTED, margin: '0 0 12px', lineHeight: 1.6, fontFamily: FONTS.sans }}>
        We can't load your restrictions right now. Please refresh the page and try again. If the problem persists, please contact us.
      </p>
    );
    body = (
      <>
        {header(overview.hideNutrition ? 'Allergies & Restrictions' : 'Nutrition Profile', { name: 'home' })}
        {!overview.hideNutrition && !overview.nutritionReviewed && (
          <p style={{
            margin: '16px 16px 0', padding: '14px 16px',
            background: COLORS.cream, borderLeft: `4px solid ${COLORS.orange}`,
            borderRadius: '8px', fontFamily: FONTS.sans, fontSize: '14px',
            lineHeight: 1.6, color: dark,
          }} role="note">
            <strong>These are preliminary results.</strong> They were generated
            automatically and a member of our nutrition team will be reviewing in
            the next couple of days. If they recommend any changes, we will inform
            you over email.
          </p>
        )}
        {overview.hideNutrition ? (
          // No mention of numbers or that anything is withheld — saying so is
          // itself the kind of prompt this exists to avoid.
          <p style={{ padding: '26px 16px', textAlign: 'center', color: MUTED, fontFamily: FONTS.sans, fontSize: '14px', lineHeight: 1.6 }}>
            Your meals are planned with you by our nutrition team. Get in touch any time and we'll talk it through.
          </p>
        ) : meals.length === 0 ? (
          <p style={{ padding: '26px 16px', textAlign: 'center', color: MUTED, fontFamily: FONTS.sans, fontSize: '14px' }}>
            No targets yet — take the quiz and we'll calculate them.
          </p>
        ) : (
          <div style={{ padding: '16px', display: 'grid', gap: '12px' }}>
            {restrictionsInCards && restrictionsUnavailableNote}
            {meals.map((m) => {
              const g = restrictionsInCards ? groupFor(m.meal) : null;
              return (
                <MacroTargetCard
                  key={m.meal}
                  title={/^Snacks?$/.test(m.meal) && m.snacksPerDay > 0 ? `${m.meal} (${m.snacksPerDay} per day)` : m.meal}
                  targets={m}
                  restrictions={g?.restrictions.map((r) => r.name)}
                  restrictionEditor={g && restrictionEditorFor(g)}
                  targetsEditor={editingMeal === m.meal && mealDraft && (
                    <MacroTargetEditor
                      embedded
                      meal={m.meal}
                      draft={mealDraft}
                      onChange={setMealDraft}
                      onSave={() => saveMealTargets(m.meal, mealDraft)}
                      onCancel={() => { setEditingMeal(null); setMealDraft(null); setSaveError(''); }}
                      saving={saving}
                      error={saveError}
                    />
                  )}
                  restrictionFooter={g && canEditRestrictions && (
                    <div>
                      <button type="button" onClick={() => startRestrictionEdit(g)} style={cardLinkStyle}>
                        Edit {m.meal.toLowerCase()} allergies
                      </button>
                    </div>
                  )}
                  footer={(
                    <button
                      type="button"
                      onClick={() => { setEditingMeal(m.meal); setMealDraft({ ...m }); setSaveError(''); }}
                      style={cardLinkStyle}
                    >
                      Edit {m.meal.toLowerCase()} targets
                    </button>
                  )}
                />
              );
            })}
          </div>
        )}

        {!restrictionsInCards && (
          <div style={{ padding: '4px 16px 20px', fontFamily: FONTS.sans }}>
            <div style={{ fontSize: '11px', letterSpacing: '1px', fontWeight: 700, color: dark, marginBottom: '10px' }}>
              ALLERGIES &amp; RESTRICTIONS
            </div>
            {restrictionsUnavailableNote}
            <div style={{ display: 'grid', gap: '18px' }}>
              {restrictionGroups.map((g) => (
                <div key={g.meal || 'all'}>
                  {g.meal && (
                    <div style={{ fontSize: '14px', fontWeight: 700, color: dark, marginBottom: '8px' }}>{g.meal}</div>
                  )}
                  {restrictionEditorFor(g) || (
                    <>
                      {g.restrictions.length ? (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {g.restrictions.map((r) => (
                            <span key={r.id} style={{
                              background: COLORS.creamAlt, border: `1px solid ${COLORS.creamAlt}`,
                              borderRadius: '20px', padding: '5px 13px', fontSize: '13px',
                              fontWeight: 700, color: dark,
                            }}>{r.name}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: '14px', color: MUTED }}>None recorded</span>
                      )}
                      {canEditRestrictions && (
                        <div>
                          <button type="button" onClick={() => startRestrictionEdit(g)} style={cardLinkStyle}>
                            Edit {g.meal ? `${g.meal.toLowerCase()} ` : ''}allergies &amp; restrictions
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  } else if (screen.name === 'subscription') {
    body = (
      <>
        {header('Subscription', { name: 'home' })}
        {subscriptions?.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 16px', borderBottom: HAIR }}>
            <button
              type="button"
              onClick={() => setShowSkipModal(true)}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                fontFamily: FONTS.sans, fontSize: '15px', fontWeight: 700, color: '#1a1a1a',
              }}
            >
              <CalendarIcon size={16} />
              <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px' }}>View delivery calendar</span>
            </button>
          </div>
        )}
        {showSkipModal && (
          <SkipDeliveriesModal
            customerToken={token}
            onClose={() => setShowSkipModal(false)}
            onSaved={handleSkipsSaved}
          />
        )}
        {subscriptions === null ? (
          <div style={{ textAlign: 'center', padding: '36px 16px', fontFamily: FONTS.sans }}>
            <p style={{ color: MUTED, fontSize: '14px', margin: '0 0 14px' }}>
              {subscriptionsError || 'Loading your subscription…'}
            </p>
            {subscriptionsError && (
              <button type="button" onClick={load} style={SAVE_BTN(loading)}>Try again</button>
            )}
          </div>
        ) : (
        <SubscriptionScreen
          subscriptions={subscriptions || []}
          onSave={patchSubscription}
          onCancel={cancelSubscription}
          onSkipDeliveries={() => setShowSkipModal(true)}
          onAddDelivery={addDelivery}
          onLoadAddOns={loadAddOnCatalog}
          onLoadDeliveryOptions={loadDeliveryOptions}
          onAddAddOn={addSubscriptionAddOn}
          onAddMeal={addSubscriptionMeal}
          defaultMealTargets={defaultMealTargets}
          mealTargets={orderedMealTargets(overview.nutritionByMeal)}
          mealPlanUrl={token ? `/meal-plan?customer=${token}` : '/meal-plan'}
          onSignup={() => { onClose(); window.location.href = '/subscription-wireframe'; }}
        />
        )}
      </>
    );
  } else if (screen.name === 'address') {
    const a = overview.address || {};
    const f = form || {
      address: a.line1 || '', address2: a.line2 || '', city: a.city || '',
      state: a.state || '', zip: a.zip || '', phone: a.phone || '',
      deliveryNotes: a.deliveryNotes || '',
    };
    // Same rule the backend re-checks on save.
    const addressValid = (!f.address || (f.city && f.state && f.zip))
      && (!f.phone || isValidPhone(f.phone));
    body = (
      <>
        {header('Address', { name: 'home' })}
        <div style={{ padding: '16px', fontFamily: FONTS.sans }}>
          {overview.addressSource && overview.addressSource !== 'client' && (
            <p style={{ fontSize: '12px', color: MUTED, margin: '0 0 14px', lineHeight: 1.6 }}>
              This is the address from your last delivery. Save it here to keep
              it on your profile.
            </p>
          )}
          <div style={{ display: 'grid', gap: '12px' }}>
            <DeliveryAddressFields
              value={f}
              onChange={(patch) => setForm({ ...f, ...patch })}
              idPrefix="account-address"
            />
          </div>

          {saveError && <p style={{ color: '#c0392b', fontSize: '13px', margin: '12px 0 0' }} role="alert">{saveError}</p>}

          <button
            type="button"
            disabled={saving || !addressValid}
            onClick={() => saveProfile(f, () => setForm(null))}
            style={{ ...SAVE_BTN(saving), marginTop: '14px', opacity: saving || !addressValid ? 0.5 : 1 }}
          >
            {saving ? 'Saving…' : 'Save address'}
          </button>

          <p style={{ fontSize: '12px', color: MUTED, margin: '10px 0 0', lineHeight: 1.6 }}>
            This updates your saved profile address. Existing orders will keep the address they were placed with — contact us if
            one of those needs to change.
          </p>
        </div>
      </>
    );
  } else if (screen.name === 'family') {
    const members = family || [];
    const setF = (k) => (e) => setFamilyForm({ ...familyForm, [k]: e.target.value });
    const canAdd = familyForm.firstName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(familyForm.email.trim());

    // Anyone without meal targets hasn't taken the quiz yet.
    const inviteButton = (name, email, hasProfile) => {
      const state = invites[email];
      if (state === 'sent') return <span style={{ fontSize: '13px', color: '#2c5f2c' }}>Invite sent to {email}</span>;
      return (
        <button
            type="button"
            disabled={state === 'sending'}
            onClick={() => inviteToQuiz({ name, email, hasProfile })}
          style={{
            background: 'none', border: `1.5px solid ${dark}`, borderRadius: '8px', padding: '8px 14px',
            fontSize: '13px', fontWeight: 700, color: dark, cursor: state === 'sending' ? 'default' : 'pointer',
            fontFamily: FONTS.sans, marginTop: '8px',
          }}
        >
          {state === 'sending' ? 'Sending…' : state === 'error' ? 'Retry Invite' : 'Send Quiz Invite'}
        </button>
      );
    };

    // Someone on their own email; a shared email already puts them in the picker.
    const accessControls = (m) => {
      if (via !== 'own') return null;
      if (m.sharesEmail) {
        return (
          <div style={{ fontSize: '13px', color: MUTED, marginTop: '8px', lineHeight: 1.5 }}>
            {m.firstName} uses your email, so you can already open their plan with Switch profile.
          </div>
        );
      }
      const key = `${m.firstName}-${m.email}`;
      const busy = accessBusy === key;
      if (m.access === 'granted') {
        return (
          <div style={{ fontSize: '13px', color: '#2c5f2c', marginTop: '8px', lineHeight: 1.5 }}>
            <div>
              You can manage {m.firstName}'s account.{' '}
              {/* The picker's list loads at sign-in, so access approved since then needs a refresh. */}
              {profiles.some((p) => p.token === m.token)
                ? <><SwitchProfileButton onClick={onClose} /> to open it.</>
                : 'Refresh the page to open it.'}
            </div>
            <div style={{ textAlign: 'right', marginTop: '4px' }}>
              <button type="button" disabled={busy} onClick={() => withdrawAccess(m)} style={LINK_BTN(MUTED)}>
                {busy ? 'Removing…' : 'Stop managing'}
              </button>
            </div>
          </div>
        );
      }
      if (m.access === 'requested') {
        return (
          <div style={{ fontSize: '13px', color: MUTED, marginTop: '8px', lineHeight: 1.5 }}>
            Access requested. Waiting for {m.firstName} to approve.{' '}
            <button type="button" disabled={busy} onClick={() => withdrawAccess(m)} style={LINK_BTN(MUTED)}>
              {busy ? 'Cancelling…' : 'Cancel request'}
            </button>
          </div>
        );
      }
      return null;
    };

    // Small link under Remove; the request is an occasional action, not the card's main one.
    const askLink = (m) => {
      if (via !== 'own' || !m.hasProfile || m.sharesEmail || (m.access && m.access !== 'none')) return null;
      const busy = accessBusy === `${m.firstName}-${m.email}`;
      return (
        <button type="button" disabled={busy} onClick={() => requestAccess(m)} style={{ ...LINK_BTN(MUTED), textAlign: 'right' }}>
          {busy ? 'Sending…' : 'Ask to manage'}
        </button>
      );
    };

    // Who asked for, or has, access to this plan; only shown on the person's own profile.
    const { requests = [], granted = [] } = (via === 'own' && access) || {};
    const card = { background: COLORS.cream, border: `1px solid ${COLORS.creamAlt}`, borderRadius: '12px', padding: '14px 16px' };
    const who = (m) => (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: FONTS.serif, fontSize: '18px', fontWeight: 700, color: dark }}>{m.name || 'Square Fare customer'}</div>
        {m.email && <div style={{ fontSize: '13px', color: MUTED, marginTop: '2px', overflowWrap: 'anywhere' }}>{m.email}</div>}
      </div>
    );
    const hasAccessList = requests.length > 0 || granted.length > 0;
    body = (
      <>
        {header('Family Members', { name: 'home' })}
        <div style={{ padding: '16px', fontFamily: FONTS.sans, display: 'grid', gap: '10px' }}>
          {requests.length > 0 && sectionHeading('ASKING TO MANAGE YOUR PLAN')}
          {requests.map((m) => {
            const busy = accessBusy === `approve-${m.token}` || accessBusy === `decline-${m.token}`;
            return (
              <div key={`r-${m.token}`} style={card}>
                {who(m)}
                <div style={{ fontSize: '13px', color: dark, margin: '8px 0', lineHeight: 1.5 }}>
                  Asked to manage your plan.
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <button type="button" disabled={busy} onClick={() => changeAccess('approve', m.token)} style={{ ...SAVE_BTN(busy), padding: '9px 20px' }}>
                    {accessBusy === `approve-${m.token}` ? 'Approving…' : 'Approve'}
                  </button>
                  <button type="button" disabled={busy} onClick={() => changeAccess('decline', m.token)} style={LINK_BTN(MUTED)}>
                    {accessBusy === `decline-${m.token}` ? 'Declining…' : 'Decline'}
                  </button>
                </div>
              </div>
            );
          })}

          {granted.length > 0 && sectionHeading('CAN ACCESS YOUR ACCOUNT')}
          {granted.length > 0 && (
            <p style={{
              margin: 0, padding: '14px 16px',
              background: COLORS.cream, borderLeft: `4px solid ${COLORS.orange}`,
              borderRadius: '8px', fontSize: '14px', lineHeight: 1.6, color: dark,
            }} role="note">
              <strong>People you approve can view and change your meals, delivery details and subscription</strong>{' '}
              from their own sign-in. You can remove their access at any time.
            </p>
          )}
          {granted.map((m) => {
            const busy = accessBusy === `revoke-${m.token}`;
            return (
              <div key={`g-${m.token}`} style={card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  {who(m)}
                  {confirmRevoke === m.token ? (
                    <div style={{ display: 'flex', gap: '12px', flexShrink: 0, paddingTop: '4px' }}>
                      <button type="button" disabled={busy} onClick={() => changeAccess('revoke', m.token)} style={LINK_BTN('#c0392b')}>
                        {busy ? 'Removing…' : 'Yes, remove'}
                      </button>
                      <button type="button" onClick={() => setConfirmRevoke(null)} style={LINK_BTN(MUTED)}>Cancel</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setConfirmRevoke(m.token)} style={{ ...LINK_BTN(MUTED), flexShrink: 0, paddingTop: '4px' }}>
                      Remove access
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {accessError && <p style={{ color: '#c0392b', fontSize: '13px', margin: 0 }} role="alert">{accessError}</p>}
          {hasAccessList && sectionHeading('YOUR HOUSEHOLD')}

          {members.length === 0 && (
            <p style={{ fontSize: '14px', color: MUTED, margin: 0, lineHeight: 1.6 }}>
              Add the people you order for. If they already have a Square Fare profile (make sure to enter the exact first name and email), we'll
              link it; if not, we'll create one and invite them to take the quiz.
            </p>
          )}

          {members.map((m) => {
            const name = [m.firstName, m.lastName].filter(Boolean).join(' ');
            const key = `${m.firstName}-${m.email}`;
            return (
              <div key={key} style={{
                background: COLORS.cream, border: `1px solid ${COLORS.creamAlt}`,
                borderRadius: '12px', padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: FONTS.serif, fontSize: '18px', fontWeight: 700, color: dark }}>{name}</div>
                    <div style={{ fontSize: '13px', color: MUTED, marginTop: '2px', overflowWrap: 'anywhere' }}>{m.email}</div>
                  </div>
                  {confirmRemove === key ? (
                    <div style={{ display: 'flex', gap: '12px', flexShrink: 0, paddingTop: '4px' }}>
                      <button type="button" disabled={familyBusy} onClick={() => removeFamilyMember(m)} style={LINK_BTN('#c0392b')}>
                        {familyBusy ? 'Removing…' : 'Yes, remove'}
                      </button>
                      <button type="button" onClick={() => setConfirmRemove(null)} style={LINK_BTN(MUTED)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0, paddingTop: '4px' }}>
                      <button type="button" onClick={() => setConfirmRemove(key)} style={LINK_BTN(MUTED)}>
                        Remove
                      </button>
                      {askLink(m)}
                    </div>
                  )}
                </div>
                {confirmRemove === key && (
                  <div style={{ fontSize: '12px', color: MUTED, marginTop: '6px', lineHeight: 1.5 }}>
                    This only removes {m.firstName} from your household — their own profile stays.
                  </div>
                )}
                {m.hasProfile ? (
                  <>
                    <div style={{ fontSize: '13px', color: dark, marginTop: '8px' }}>
                      {/* {m.meals.map((x) => x.meal).join(' · ')} targets set */}
                    </div>
                    {accessControls(m)}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '13px', color: '#8a4b1f', marginTop: '8px' }}>
                      No nutrition profile yet — Please invite them to take the
                      quiz so we can tailor their meals.
                    </div>
                    {inviteButton(name, m.email, m.hasProfile)}
                  </>
                )}
              </div>
            );
          })}

          {sectionHeading('ADD A FAMILY MEMBER')}
          {[['firstName', 'First name', true], ['lastName', 'Last name', false], ['email', 'Email address', true]].map(([key, label, required]) => (
            <label key={key} style={{ display: 'block' }}>
              <span style={{ fontSize: '12px', color: MUTED, display: 'block', marginBottom: '4px' }}>
                {label}
                {required && <span style={{ color: '#c0392b', marginLeft: '3px' }}>*</span>}
              </span>
              <input
                type={key === 'email' ? 'email' : 'text'}
                value={familyForm[key]}
                onChange={setF(key)}
                required={required}
                aria-required={required}
                style={FIELD}
              />
            </label>
          ))}

          {familyError && <p style={{ color: '#c0392b', fontSize: '13px', margin: 0 }} role="alert">{familyError}</p>}
          {familyNotice && <p style={{ color: '#2c5f2c', fontSize: '13px', margin: 0 }} role="status">{familyNotice}</p>}

          <button
            type="button"
            disabled={familyBusy || !canAdd}
            onClick={addFamilyMember}
            style={{ ...SAVE_BTN(familyBusy), opacity: familyBusy || !canAdd ? 0.6 : 1 }}
          >
            {familyBusy ? 'Adding…' : 'Add family member'}
          </button>
        </div>
      </>
    );
  } else if (screen.name === 'profile') {
    const pf = form || {
      firstName: overview.profile.firstName || '',
      lastName: overview.profile.lastName || '',
      email: overview.profile.email || '',
    };
    const setP = (k) => (e) => setForm({ ...pf, [k]: e.target.value });
    const norm = (v) => String(v || '').trim().toLowerCase();
    const newEmail = norm(pf.email);
    // Moving the email this login signs in with takes the plan away from it, so sign out with the save.
    const signsOut = via === 'own' && Boolean(newEmail)
      && norm(overview.profile.email) === norm(user?.email)
      && newEmail !== norm(overview.profile.email);
    body = (
      <>
        {header('Profile', { name: 'home' })}
        <div style={{ padding: '4px 16px', fontFamily: FONTS.sans }}>
          <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED, margin: '16px 0 6px' }}>NAME</div>
          <label style={{ display: 'block', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', color: MUTED, display: 'block', marginBottom: '4px' }}>First</span>
            <input value={pf.firstName} onChange={setP('firstName')} style={FIELD} />
          </label>
          <label style={{ display: 'block', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', color: MUTED, display: 'block', marginBottom: '4px' }}>Last</span>
            <input value={pf.lastName} onChange={setP('lastName')} style={FIELD} />
          </label>
          <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED, margin: '18px 0 6px' }}>EMAIL</div>
          <label style={{ display: 'block', marginBottom: '12px' }}>
            <input type="email" value={pf.email} onChange={setP('email')} style={FIELD} />
          </label>
          {signsOut && (
            <p style={{ fontSize: '13px', color: '#c0392b', lineHeight: 1.5, margin: '-4px 0 12px' }}>
              You'll be signed out. Please sign back in with {newEmail} to access your account.
            </p>
          )}

          {saveError && <p style={{ color: '#c0392b', fontSize: '13px', margin: '0 0 10px' }} role="alert">{saveError}</p>}

          <button
            type="button"
            disabled={saving}
            onClick={() => saveProfile(pf, signsOut ? onSignOut : () => setForm(null))}
            style={SAVE_BTN(saving)}
          >
            {saving ? 'Saving…' : (signsOut ? 'Save Changes and Sign Out' : 'Save Changes')}
          </button>

          <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED, margin: '18px 0 6px' }}>SIGNING IN</div>
          <p style={{ fontSize: '13px', color: MUTED, lineHeight: 1.6, margin: '4px 0 20px' }}>
            There's no password to manage — we email you a sign-in code each time.
          </p>
        </div>
      </>
    );
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }}
      role="dialog"
      aria-label="Account"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(420px, 100%)',
          background: '#fff', overflowY: 'auto', boxShadow: '-12px 0 34px rgba(0,0,0,0.18)',
        }}
      >
        {body}
        {reordered && (
          <div style={{
            position: 'sticky', bottom: '14px', margin: '0 16px', padding: '11px 14px',
            background: reordered.ok ? '#2c5f2c' : '#8a4b1f', color: '#fff', borderRadius: '10px',
            fontSize: '14px', textAlign: 'center', fontFamily: FONTS.sans,
          }} role="status">
            {reordered.ok
              ? `${reordered.name} added to your cart`
              : `${reordered.name} isn't on this week's menu`}
          </div>
        )}
      </div>
    </div>
  );
}
